import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { X, Minus, Plus, ShoppingCart, Trash2, ArrowRight } from 'lucide-react';

// Helper function to format currency
const formatCurrency = (amount) => {
  return `₱${Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const CartModal = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    isCartOpen,
    closeCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalPrice,
    isLoggedIn
  } = useCart();

  if (!isCartOpen) return null;

  const handleProceedToBooking = () => {
    closeCart();
    navigate('/book/venue_camsur');
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[100] transition-opacity"
        onClick={closeCart}
      />
      
      {/* Modal */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[101] flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-[#004a80]">
          <div className="flex items-center gap-3">
            <ShoppingCart className="h-6 w-6 text-white" />
            <h2 className="text-xl font-bold text-white">Your Cart</h2>
            {cartItems.length > 0 && (
              <span className="bg-[#c5e153] text-[#004a80] text-sm font-bold px-2 py-0.5 rounded-full">
                {cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0)}
              </span>
            )}
          </div>
          <button 
            onClick={closeCart}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {!isLoggedIn ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <ShoppingCart className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Please Log In</h3>
              <p className="text-gray-500 mb-6">You need to be logged in to use the cart feature.</p>
              <Link 
                to="/login" 
                onClick={closeCart}
                className="bg-[#004a80] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#003a60] transition-colors"
              >
                Log In
              </Link>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <ShoppingCart className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Your cart is empty</h3>
              <p className="text-gray-500 mb-6">Add some items from the shop to get started!</p>
              <button
                onClick={() => {
                  closeCart();
                  const element = document.getElementById('addons');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-[#004a80] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#003a60] transition-colors"
              >
                Browse Shop
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => {
                const itemId = item.item_id || item.equipment_id;
                const price = item.price || item.price_per_hour || 0;
                
                return (
                  <div 
                    key={itemId}
                    className="flex gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100"
                  >
                    {/* Image */}
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/newassets/placeholder.png';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingCart className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-1">
                        {item.name}
                      </h4>
                      {item.category && (
                        <span className="inline-block text-xs bg-[#c5e153] text-[#004a80] px-2 py-0.5 rounded-full font-medium mb-2">
                          {item.category}
                        </span>
                      )}
                      <div className="flex items-center justify-between">
                        <p className="font-bold text-[#004a80]">
                          {formatCurrency(price * (item.quantity || 1))}
                        </p>
                        
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(itemId, (item.quantity || 1) - 1)}
                            className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                          >
                            <Minus className="h-4 w-4 text-gray-700" />
                          </button>
                          <span className="w-8 text-center font-semibold text-gray-900">
                            {item.quantity || 1}
                          </span>
                          <button
                            onClick={() => updateQuantity(itemId, (item.quantity || 1) + 1)}
                            className="w-7 h-7 rounded-full bg-[#c5e153] hover:bg-[#b5d143] flex items-center justify-center transition-colors"
                          >
                            <Plus className="h-4 w-4 text-[#004a80]" />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Remove Button */}
                    <button
                      onClick={() => removeFromCart(itemId)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full self-start transition-colors"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {isLoggedIn && cartItems.length > 0 && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            {/* Total */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600 font-medium">Subtotal</span>
              <span className="text-xl font-bold text-[#004a80]">
                {formatCurrency(getTotalPrice())}
              </span>
            </div>
            
            {/* Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> These items will be added to your booking. Complete your court reservation to finalize the purchase.
              </p>
            </div>
            
            {/* Actions */}
            <div className="space-y-2">
              <button
                onClick={handleProceedToBooking}
                className="w-full bg-[#004a80] text-white py-3 px-4 rounded-full font-bold flex items-center justify-center gap-2 hover:bg-[#003a60] transition-colors"
              >
                Proceed to Booking
                <ArrowRight className="h-5 w-5" />
              </button>
              <button
                onClick={clearCart}
                className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-full font-medium hover:bg-gray-300 transition-colors text-sm"
              >
                Clear Cart
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default CartModal;
